import { usePageSEO } from '@/hooks/usePageSEO';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import PourquoiLeBardeau from './components/PourquoiLeBardeau';
import GammesBardeau from './components/GammesBardeau';
import Marques from './components/Marques';
import PrixBardeau from './components/PrixBardeau';
import Couleurs from './components/Couleurs';
import Entretien from './components/Entretien';
import FAQ from './components/FAQ';
import QuoteForm from './components/QuoteForm';
import CTAFinal from './components/CTAFinal';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://couvreur-levis.com/' },
    { '@type': 'ListItem', position: 2, name: 'Bardeau asphalte', item: 'https://couvreur-levis.com/bardeau-asphalte' },
  ],
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: "Pose de bardeau d'asphalte",
  provider: { '@type': 'LocalBusiness', '@id': 'https://couvreur-levis.com/#business' },
  areaServed: { '@type': 'City', name: 'Lévis' },
  description: "Pose de bardeau d'asphalte à Lévis. Marques IKO, BP, GAF. Garantie fabricant 25-50 ans.",
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'CAD',
    lowPrice: '7000',
    highPrice: '19000',
    offerCount: '3',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: "Combien de temps dure vraiment un bardeau d'asphalte à Lévis ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Un bardeau standard de qualité dure 15 à 20 ans dans le climat de Lévis. Le bardeau architectural (gamme la plus populaire) dure 25 à 30 ans. Les facteurs clés sont : la qualité de la membrane d'étanchéité sous le bardeau, la ventilation du toit (qui élimine l'humidité), et la qualité de l'installation. Un bardeau mal ventilé ou posé sans membrane sous-jacente peut détériorer en 10 ans.",
      },
    },
    {
      '@type': 'Question',
      name: "Peut-on poser un nouveau bardeau par-dessus l'ancien ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Techniquement oui, mais ce n'est pas recommandé. Poser par-dessus empêche d'inspecter le pontage et la membrane sous-jacente. Si le bardeau inférieur est humide, ça crée un sandwich d'humidité qui abîme les deux couches. On recommande l'enlèvement complet — ça coûte un peu plus, mais ça donne accès à la structure et permet une installation conforme aux normes du RBQ.",
      },
    },
    {
      '@type': 'Question',
      name: "Quelle est la différence entre BP, IKO et GAF ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "BP est fabriqué à Lachine, au Québec — c'est la marque la plus installée par nos partenaires grâce à son excellent rapport qualité-prix et sa disponibilité locale. IKO fabrique en Ontario et est très populaire pour sa gamme Cambridge, connue pour sa résistance au vent. GAF est la marque américaine #1 en Amérique du Nord, avec des gammes premium comme Timberline HDZ. Les trois offrent des garanties de 25 à 50 ans. On vous conseille la marque adaptée à votre budget et à vos besoins.",
      },
    },
    {
      '@type': 'Question',
      name: 'Quelle couleur de bardeau est la plus durable ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Les couleurs foncées (noir, brun foncé) absorbent plus de chaleur solaire, ce qui peut accélérer légèrement le vieillissement. Les couleurs claires ou intermédiaires (gris ardoise, brun moyen, sable) tendent à durer un peu plus longtemps. Cela dit, l'impact est marginal — la qualité de l'installation et la ventilation comptent beaucoup plus que la couleur. Toutes les couleurs que nous proposons sont certifiées pour résister aux UV québécois.",
      },
    },
    {
      '@type': 'Question',
      name: 'Le bardeau résiste-t-il aux vents extrêmes ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Le bardeau architectural (notre gamme standard) est classé pour résister à des vents jusqu'à 180 km/h quand il est correctement installé avec des clous résistants aux vents (par opposition aux agrafes). Dans notre région du Saint-Laurent, où les vents peuvent être forts, on utilise toujours la technique des 6 clous par bardeau (au lieu des 4 minimum) et on scelle les cônes de toit. La garantie fabricant couvre aussi les dégâts de vent dans la plupart des cas.",
      },
    },
  ],
};

export default function BardeauPage() {
  usePageSEO({
    title: "Bardeau d'asphalte Lévis | IKO, BP, GAF | Pose pro",
    description: "Pose de bardeau d'asphalte à Lévis. Marques IKO, BP, GAF. Garantie fabricant 25-50 ans. Soumission gratuite en 2h. 418-476-9950.",
    urlPath: '/bardeau-asphalte',
    keywords: 'bardeau asphalte Lévis, shingle toiture, IKO Lévis, BP bardeau, GAF toiture, couvreur',
    ogImage: '/og/og-bardeau-asphalte.jpg',
    ogImageAlt: "Bardeau d'asphalte à Lévis - IKO, BP, GAF garantie 25-50 ans",
    jsonLdSchema: [breadcrumbSchema, serviceSchema, faqSchema],
  });

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <PourquoiLeBardeau />
        <GammesBardeau />
        <Marques />
        <PrixBardeau />
        <Couleurs />
        <Entretien />
        <FAQ />
        <QuoteForm />
        <CTAFinal />
      </main>
      <Footer />
    </div>
  );
}